/**
 * The over-the-shoulder camera and where its reticle points, in one place.
 *
 * The camera sits behind, above and to the right of the fighter's eyes and
 * looks at a focus point on the eye's look line. At the focus distance the
 * reticle and the eye agree; closer, they do not: at 1 m the reticle shows a
 * point ≈ 0.23 m higher and ≈ 0.33 m further right than the eye's line. The
 * simulation aims (and guards) along the eye's line, so the client sends the
 * direction from the eyes to whatever is under the reticle (convergence, as
 * third-person shooters do from the muzzle). Nothing under the reticle: the
 * focus point, which is exactly the mouse's own direction.
 */
import { closestPointsSegments, type Vec3, type Volume } from "@meleeroyale/sim";

export const CAMERA = {
  /** Distance behind the eyes along the look, alive and spectating. */
  back: 3.2,
  backDead: 5.5,
  /** Offset to the right of the look (over the right shoulder) and up. */
  right: 0.8,
  up: 0.55,
  /** Where the camera looks: this far along the eye's look line. */
  focus: 4,
  /** How far past the focus point the reticle may still find a fighter. */
  beyond: 2,
} as const;

export interface CameraRay {
  position: Vec3;
  focus: Vec3;
}

/** Camera position and focus for eyes at `eye` looking along yaw/pitch (radians). */
export function cameraRig(eye: Vec3, yaw: number, pitch: number, alive: boolean): CameraRay {
  const cp = Math.cos(pitch);
  const f = { x: Math.sin(yaw) * cp, y: Math.sin(pitch), z: Math.cos(yaw) * cp };
  // Right of the screen for a camera looking along `f` (see controls.ts).
  const r = { x: -Math.cos(yaw), y: 0, z: Math.sin(yaw) };
  const back = alive ? CAMERA.back : CAMERA.backDead;
  return {
    position: {
      x: eye.x - f.x * back + r.x * CAMERA.right,
      y: Math.max(0.3, eye.y - f.y * back + CAMERA.up),
      z: eye.z - f.z * back + r.z * CAMERA.right,
    },
    focus: {
      x: eye.x + f.x * CAMERA.focus,
      y: eye.y + f.y * CAMERA.focus,
      z: eye.z + f.z * CAMERA.focus,
    },
  };
}

/**
 * Yaw and pitch (radians) from the eyes to what the reticle is on: the
 * nearest of `targets` (other fighters' hurt volumes, as drawn) along the
 * camera's centre ray, else the focus point.
 */
export function convergeAim(
  eye: Vec3,
  yaw: number,
  pitch: number,
  targets: readonly Volume[],
): { yaw: number; pitch: number } {
  const rig = cameraRig(eye, yaw, pitch, true);
  const c = rig.position;
  const dx = rig.focus.x - c.x;
  const dy = rig.focus.y - c.y;
  const dz = rig.focus.z - c.z;
  const toFocus = Math.hypot(dx, dy, dz);
  const reach = (toFocus + CAMERA.beyond) / toFocus;
  const far = { x: c.x + dx * reach, y: c.y + dy * reach, z: c.z + dz * reach };
  let best = Number.POSITIVE_INFINITY;
  for (const v of targets) {
    const q = closestPointsSegments(c, far, v.a, v.b);
    if (q.dist2 <= v.r * v.r && q.s < best) best = q.s;
  }
  const point =
    best < Number.POSITIVE_INFINITY
      ? {
          x: c.x + (far.x - c.x) * best,
          y: c.y + (far.y - c.y) * best,
          z: c.z + (far.z - c.z) * best,
        }
      : rig.focus;
  const px = point.x - eye.x;
  const py = point.y - eye.y;
  const pz = point.z - eye.z;
  return { yaw: Math.atan2(px, pz), pitch: Math.atan2(py, Math.hypot(px, pz)) };
}
